import { useState } from "react";
import { type Control, type FieldPath, type FieldValues } from "react-hook-form";
import { ApiError } from "@/lib/apiClient";
import {
  AsyncSearchableSelect,
  type SearchableOption,
} from "@/components/SearchableSelect";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { tickets as ticketsApi } from "@/features/tickets/api/tickets.api";
import type { UserRef } from "@/utils/userRef";

const ASSIGNEE_PAGE_SIZE = 20;
export const UNASSIGNED = "__unassigned__";

type LoadError = { kind: "forbidden" } | { kind: "failed"; message: string };

async function searchAssignees({ keyword, pageNumber }: { keyword: string; pageNumber: number }) {
  const page = await ticketsApi.assignableUsers({
    keyword,
    pageNumber,
    pageSize: ASSIGNEE_PAGE_SIZE,
  });
  return {
    options: page.items.map((u) => ({ value: u.id, label: u.name, hint: u.email ?? undefined })),
    hasNextPage: page.hasNextPage,
    totalCount: page.totalCount,
  };
}

function pinnedOptions(current: UserRef | null): SearchableOption[] {
  const options: SearchableOption[] = [{ value: UNASSIGNED, label: "Unassigned" }];
  if (current?.id) {
    options.push({ value: current.id, label: current.name || current.email || current.id });
  }
  return options;
}

export function AssigneeField<T extends FieldValues>({
  control,
  name,
  current = null,
  className,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  current?: UserRef | null;
  className?: string;
}) {
  const [loadError, setLoadError] = useState<LoadError | null>(null);

  function onError(e: unknown) {
    setLoadError(
      e instanceof ApiError && e.status === 403
        ? { kind: "forbidden" }
        : {
            kind: "failed",
            message: e instanceof Error ? e.message : "Failed to load the assignee list.",
          },
    );
  }

  if (loadError?.kind === "forbidden") {
    return (
      <FormItem className={className}>
        <FormLabel>Assigned to</FormLabel>
        <p className="text-sm text-slate-900">
          {current?.name || <span className="text-slate-400">Unassigned</span>}
        </p>
      </FormItem>
    );
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>Assigned to</FormLabel>
          <FormControl>
            <AsyncSearchableSelect
              ref={field.ref}
              value={field.value || UNASSIGNED}
              onValueChange={(v) => field.onChange(v === UNASSIGNED ? "" : v)}
              searchPlaceholder="Search users…"
              search={searchAssignees}
              pinnedOptions={pinnedOptions(current)}
              onError={onError}
            />
          </FormControl>
          {loadError?.kind === "failed" && (
            <FormDescription className="text-red-600">
              {loadError.message}{" "}
              <button
                type="button"
                onClick={() => setLoadError(null)}
                className="underline hover:no-underline"
              >
                Try again
              </button>
            </FormDescription>
          )}
        </FormItem>
      )}
    />
  );
}
